/**
 * Admin Access Control
 *
 * Controls access to the admin subdomain (admin.subvention360.com)
 * Only specific emails can access admin functionality.
 */

// Allowed admin emails (hardcoded for security)
export const ADMIN_ALLOWED_EMAILS = [
  'wilfrid.rimbault@subvention360.com',
  'thomas.wainstein@subvention360.com',
  'thomaswainstein@gmail.com',
  'thomas.musikas@gmail.com',
  'admin@subvention360.com',
] as const;

// Admin subdomains (only production admin subdomain)
// Note: localhost is NOT included - users can manually navigate to /admin-v2 for development
const ADMIN_HOSTNAMES = [
  'admin.subvention360.com',
] as const;

/**
 * Check if current hostname is an admin domain
 */
export function isAdminDomain(): boolean {
  const hostname = window.location.hostname;
  return ADMIN_HOSTNAMES.some(h => hostname === h || hostname.startsWith('admin.'));
}

/**
 * Check if email is in the allowed admin list
 */
export function isAllowedAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return ADMIN_ALLOWED_EMAILS.includes(email.toLowerCase() as any);
}

/**
 * Check if user has admin access (correct domain + allowed email)
 */
export function hasAdminAccess(email: string | null | undefined): boolean {
  return isAdminDomain() && isAllowedAdminEmail(email);
}

/**
 * Get redirect URL for unauthorized access
 */
export function getUnauthorizedRedirect(): string {
  const hostname = window.location.hostname;

  // On localhost, redirect to local root (not production)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/';
  }

  // If on admin domain but not authorized, redirect to main app
  if (isAdminDomain()) {
    return 'https://subvention360.com';
  }
  return '/';
}

/**
 * Admin access denied reasons
 */
export type AdminAccessDeniedReason =
  | 'not_admin_domain'
  | 'email_not_allowed'
  | 'not_authenticated';

/**
 * Check admin access and return reason if denied
 */
export function checkAdminAccess(email: string | null | undefined): {
  allowed: boolean;
  reason?: AdminAccessDeniedReason;
} {
  const hostname = window.location.hostname;
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';

  // On localhost, allow access without authentication for development
  // This lets developers test admin pages without needing valid Supabase credentials
  if (isLocalDev) {
    return { allowed: true };
  }

  // On production, require admin domain
  if (!isAdminDomain()) {
    return { allowed: false, reason: 'not_admin_domain' };
  }

  if (!email) {
    return { allowed: false, reason: 'not_authenticated' };
  }

  if (!isAllowedAdminEmail(email)) {
    return { allowed: false, reason: 'email_not_allowed' };
  }

  return { allowed: true };
}
